import React from 'react';
import { Package, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { formatSellingUnit, formatUnitConversion } from './catalogUnits';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

const isAvailable = (product) => product.availability
  ? product.availability === 'in_stock'
  : Number(product.stock) > 0;

const ProductList = ({ loading, products, onToggleActive, onEdit, onDelete }) => (
  <Card>
    <CardContent className="p-0">
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No products found</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="products-table">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left p-4 font-semibold">Product</th>
                <th className="text-left p-4 font-semibold">Category</th>
                <th className="text-right p-4 font-semibold">Price / unit</th>
                <th className="text-center p-4 font-semibold">Availability</th>
                <th className="text-center p-4 font-semibold">MOQ</th>
                <th className="text-center p-4 font-semibold">Images</th>
                <th className="text-center p-4 font-semibold">Status</th>
                <th className="text-center p-4 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => {
                const pid = product._id || product.id;
                return (
                  <tr key={pid} className={`border-b hover:bg-gray-50 ${!product.is_active ? 'opacity-50' : ''}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded bg-gray-100 overflow-hidden">
                          {product.imageUrl || product.images?.[0]?.url ? (
                            <img
                              src={getImageUrl(product.imageUrl || product.images[0].url)}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="h-6 w-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-semibold">{product.name}</p>
                          <p className="text-xs text-gray-500 line-clamp-1">{product.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="px-2 py-1 bg-gray-100 rounded text-sm">
                        {product.category || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="p-4 text-right font-semibold" data-testid={`product-price-${pid}`}>
                      ₦{product.price?.toLocaleString()}
                      <span className="block text-xs font-normal text-gray-500">per {formatSellingUnit(product.unit_value, product.unit)}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-sm ${isAvailable(product) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-testid={`product-availability-${pid}`}>
                        {isAvailable(product) ? 'Available' : 'Out of stock'}
                      </span>
                    </td>
                    <td className="p-4 text-center text-sm">
                      <span data-testid={`product-moq-${pid}`}>{product.min_order_quantity || 1} sale unit{product.min_order_quantity > 1 ? 's' : ''}</span>
                      <span className="block text-xs text-gray-500">{formatUnitConversion(product.unit_value, product.unit)}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className="text-sm text-gray-600">
                        {product.images?.length || (product.imageUrl ? 1 : 0)}
                      </span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        product.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {product.is_active !== false ? 'Active' : 'Hidden'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => onToggleActive(product)}
                          className={`p-1.5 rounded ${product.is_active !== false ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'}`}
                          title={product.is_active !== false ? 'Hide' : 'Show'}
                          data-testid={`toggle-active-${pid}`}
                        >
                          {product.is_active !== false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => onEdit(product)}
                          className="p-1.5 rounded bg-blue-100 text-blue-600 hover:bg-blue-200"
                          title="Edit"
                          data-testid={`edit-product-${pid}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => onDelete(pid, product.name)}
                          className="p-1.5 rounded bg-red-100 text-red-600 hover:bg-red-200"
                          title="Delete"
                          data-testid={`delete-product-${pid}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

export default ProductList;
